import { useCallback, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Plus, X } from 'lucide-react'
import { clsx } from 'clsx'
import { SearchInput } from '../ui/SearchInput'
import { useDismiss } from '../../hooks/useDismiss'
import { MOBILE_MEDIA_QUERY, useMediaQuery } from '../../hooks/useMediaQuery'
import styles from './FilterBar.module.css'

export type FilterBarOption = { value: string; label: string }

type FieldBase = {
  id: string
  label: string
  /** Shown in place of the choices, e.g. "Choose a game first". */
  unavailableHint?: string
}

export type FilterField = FieldBase &
  (
    | { kind: 'multi'; options: FilterBarOption[]; selected: readonly string[]; onToggle: (value: string, on: boolean) => void }
    | { kind: 'single'; options: FilterBarOption[]; value: string; defaultValue?: string; onChange: (value: string) => void }
    | { kind: 'toggle'; active: boolean; onChange: (active: boolean) => void }
    | {
        kind: 'range'
        inputType: 'number' | 'date'
        prefix?: string
        min: string
        max: string
        onMinChange: (value: string) => void
        onMaxChange: (value: string) => void
        /** Runs when the editor closes, for pages that apply a typed value on blur. */
        onDone?: () => void
      }
  )

export type FilterBarChip = {
  key: string
  fieldId: string
  name?: string
  value: string
  onRemove: () => void
}

type Props = {
  fields: FilterField[]
  /** Replaces the chips worked out from `fields`, for a page whose chips read differently. */
  chips?: FilterBarChip[]
  onClearAll?: () => void
  /** Show Clear with no chips too, e.g. when only the search box is in use. */
  alwaysShowClear?: boolean
  className?: string
  /** Held to the right of the chips (a page's layout switch): view options sit beside the filters. */
  trailing?: ReactNode
}

const SEARCHABLE_FROM = 8
/** All 1,300 sets as radios is slow on a phone, and nobody scrolls that far: the box narrows them. */
const MAX_SHOWN_OPTIONS = 150

const rangeChipValue = (prefix: string, min: string, max: string) => {
  if (min && max) return `${prefix}${min} – ${prefix}${max}`
  return min ? `from ${prefix}${min}` : `up to ${prefix}${max}`
}

/** Each active value as a chip: one per multi-select value, one for anything else that is set. */
export function chipsForFields(fields: FilterField[]): FilterBarChip[] {
  const chips: FilterBarChip[] = []
  for (const field of fields) {
    if (field.kind === 'multi') {
      for (const value of field.selected) {
        chips.push({
          key: `${field.id}:${value}`,
          fieldId: field.id,
          name: field.label,
          value: field.options.find((option) => option.value === value)?.label ?? value,
          onRemove: () => field.onToggle(value, false),
        })
      }
    } else if (field.kind === 'single') {
      const defaultValue = field.defaultValue ?? ''
      if (field.value !== defaultValue) {
        chips.push({
          key: field.id,
          fieldId: field.id,
          name: field.label,
          value: field.options.find((option) => option.value === field.value)?.label ?? field.value,
          onRemove: () => field.onChange(defaultValue),
        })
      }
    } else if (field.kind === 'toggle') {
      if (field.active) {
        chips.push({ key: field.id, fieldId: field.id, value: field.label, onRemove: () => field.onChange(false) })
      }
    } else if (field.min || field.max) {
      chips.push({
        key: field.id,
        fieldId: field.id,
        name: field.label,
        value: rangeChipValue(field.prefix ?? '', field.min, field.max),
        onRemove: () => {
          field.onMinChange('')
          field.onMaxChange('')
          field.onDone?.()
        },
      })
    }
  }
  return chips
}

/**
 * The one way to filter a list or a search: each active filter is a chip, click it to change it
 * and × to drop it, and "Add filter" lists the rest. It holds no filter state; the page says what
 * each field's value is and how to change it. On phones the menus are a bottom sheet on <body>, so
 * no fixed header, pager or nav can cover them.
 */
export function FilterBar({ fields, chips, onClearAll, alwaysShowClear = false, className, trailing }: Props) {
  // null = closed; 'menu' = the list of filters; otherwise the id of the field being edited.
  const [open, setOpen] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const isPhone = useMediaQuery(MOBILE_MEDIA_QUERY)

  const editing = open && open !== 'menu' ? fields.find((field) => field.id === open) ?? null : null

  // Every way out of an editor goes through here, so a price typed but not yet applied is never lost.
  const openOrClose = useCallback((next: string | null) => {
    if (editing?.kind === 'range') editing.onDone?.()
    setOpen(next)
  }, [editing])
  const close = useCallback(() => openOrClose(null), [openOrClose])
  useDismiss(ref, open !== null, close)

  const shownChips = useMemo(() => chips ?? chipsForFields(fields), [chips, fields])

  const pick = (field: FilterField) => {
    if (field.kind === 'toggle' && !field.unavailableHint) {
      field.onChange(true)
      close()
    } else {
      openOrClose(field.id)
    }
  }

  const popover = open !== null && (
    <>
      <div className={styles.menuBackdrop} onClick={close} aria-hidden data-dismiss-keep="" />
      {open === 'menu' ? (
        <div className={styles.menu} role="menu" aria-label="Add filter" data-dismiss-keep="">
          {fields.map((field) => (
            <button key={field.id} type="button" role="menuitem" className={styles.viewItem} onClick={() => pick(field)}>
              {field.label}
            </button>
          ))}
        </div>
      ) : editing ? (
        <div className={`${styles.menu} ${styles.editor}`} role="dialog" aria-label={`${editing.label} filter`} data-dismiss-keep="">
          <FilterEditor field={editing} onDone={close} />
        </div>
      ) : null}
    </>
  )

  return (
    <div className={clsx(styles.chipsBar, className)} ref={ref}>
      {shownChips.map((chip) => (
        <span key={chip.key} className={styles.chip}>
          <button type="button" className={styles.chipLabel} onClick={() => openOrClose(chip.fieldId)} title={`Change ${chip.name || chip.value}`}>
            {chip.name && <span className={styles.chipName}>{chip.name}:</span>} {chip.value}
          </button>
          <button
            type="button"
            className={styles.chipRemove}
            aria-label={`Remove filter ${chip.name ? `${chip.name} ` : ''}${chip.value}`}
            onClick={chip.onRemove}
          >
            <X size={12} aria-hidden />
          </button>
        </span>
      ))}

      <div className={styles.menuAnchor}>
        <button
          type="button"
          className={styles.addFilterButton}
          onClick={() => openOrClose(open === 'menu' ? null : 'menu')}
          aria-expanded={open === 'menu'}
          aria-haspopup="true"
        >
          <Plus size={14} aria-hidden /> Add filter
        </button>
        {isPhone && popover ? createPortal(popover, document.body) : popover}
      </div>

      {onClearAll && (shownChips.length > 0 || alwaysShowClear) && (
        <button type="button" className={styles.clearButton} onClick={onClearAll}>
          Clear
        </button>
      )}

      {trailing ? <div className={styles.trailing}>{trailing}</div> : null}
    </div>
  )
}

/** The body of one filter's popover: checkboxes, a choice, or two boxes for a range. */
function FilterEditor({ field, onDone }: { field: FilterField; onDone: () => void }) {
  const [needle, setNeedle] = useState('')

  if (field.unavailableHint) {
    return (
      <div className={styles.editorBody}>
        <div className={styles.editorTitle}>{field.label}</div>
        <div className={styles.editorHint}>{field.unavailableHint}</div>
        <button type="button" className={styles.primarySmall} onClick={onDone}>Done</button>
      </div>
    )
  }

  if (field.kind === 'range') {
    const label = field.label.toLowerCase()
    const isPrice = field.inputType === 'number'
    const applyOnEnter = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return
      event.preventDefault()
      onDone()
    }
    return (
      <div className={styles.editorBody}>
        <div className={styles.editorTitle}>{field.label}{field.prefix ? ` (${field.prefix})` : ''}</div>
        <div className={styles.priceRow}>
          <input
            type={field.inputType}
            className={styles.textInput}
            placeholder={`Min ${field.prefix ?? ''}`.trim()}
            value={field.min}
            onChange={(event) => field.onMinChange(event.target.value)}
            {...(isPrice ? { min: '0', step: '0.01' } : {})}
            onKeyDown={applyOnEnter}
            enterKeyHint="done"
            aria-label={`Minimum ${label} filter`}
            autoFocus
          />
          <span>to</span>
          <input
            type={field.inputType}
            className={styles.textInput}
            placeholder={`Max ${field.prefix ?? ''}`.trim()}
            value={field.max}
            onChange={(event) => field.onMaxChange(event.target.value)}
            {...(isPrice ? { min: '0', step: '0.01' } : {})}
            onKeyDown={applyOnEnter}
            enterKeyHint="done"
            aria-label={`Maximum ${label} filter`}
          />
        </div>
        <button type="button" className={styles.primarySmall} onClick={onDone}>Done</button>
      </div>
    )
  }

  if (field.kind === 'single' || field.kind === 'toggle') {
    const choices = field.kind === 'single' ? field.options : [{ value: '', label: 'Off' }, { value: '1', label: 'On' }]
    const current = field.kind === 'single' ? field.value : field.active ? '1' : ''
    const shown = needle ? choices.filter((choice) => choice.label.toLowerCase().includes(needle.toLowerCase())) : choices
    const choose = (value: string) => {
      if (field.kind === 'single') field.onChange(value)
      else field.onChange(value === '1')
      onDone()
    }
    return (
      <div className={styles.editorBody} role="radiogroup" aria-label={field.label}>
        <div className={styles.editorTitle}>{field.label}</div>
        {choices.length > SEARCHABLE_FROM && (
          <SearchInput
            size="sm"
            value={needle}
            onValueChange={setNeedle}
            placeholder={`Find a ${field.label.toLowerCase()}…`}
            ariaLabel={`Find a ${field.label.toLowerCase()}`}
            autoFocus
          />
        )}
        <div className={styles.optionList}>
          {shown.length === 0 && <div className={styles.editorHint}>Nothing to choose from yet.</div>}
          {shown.slice(0, MAX_SHOWN_OPTIONS).map((choice) => (
            <label key={choice.value || 'default'} className={styles.optionRow}>
              <input type="radio" name={`filter-${field.id}`} checked={current === choice.value} onChange={() => choose(choice.value)} />
              {choice.label}
            </label>
          ))}
          {shown.length > MAX_SHOWN_OPTIONS && <div className={styles.editorHint}>Showing {MAX_SHOWN_OPTIONS} of {shown.length}. Type to narrow.</div>}
        </div>
      </div>
    )
  }

  const shown = needle ? field.options.filter((option) => option.label.toLowerCase().includes(needle.toLowerCase())) : field.options
  return (
    <div className={styles.editorBody}>
      <div className={styles.editorTitle}>{field.label} <span className={styles.editorHint}>any of</span></div>
      {field.options.length > SEARCHABLE_FROM && (
        <SearchInput
          size="sm"
          value={needle}
          onValueChange={setNeedle}
          placeholder={`Find a ${field.label.toLowerCase()}…`}
          ariaLabel={`Find a ${field.label.toLowerCase()}`}
          autoFocus
        />
      )}
      <div className={styles.optionList}>
        {shown.length === 0 && <div className={styles.editorHint}>Nothing to choose from yet.</div>}
        {shown.slice(0, MAX_SHOWN_OPTIONS).map((option) => (
          <label key={option.value} className={styles.optionRow}>
            <input type="checkbox" checked={field.selected.includes(option.value)} onChange={(event) => field.onToggle(option.value, event.target.checked)} />
            {option.label}
          </label>
        ))}
        {shown.length > MAX_SHOWN_OPTIONS && <div className={styles.editorHint}>Showing {MAX_SHOWN_OPTIONS} of {shown.length}. Type to narrow.</div>}
      </div>
      <button type="button" className={styles.primarySmall} onClick={onDone}>Done</button>
    </div>
  )
}
